import React, { useEffect, useState } from 'react';
import { Box, Typography, Input, Textarea, Button } from '@mui/joy';
import { ChevronLeft, Trash2 } from 'lucide-react';
import CategorySectionsEditor from '../../components/CategoryTab';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authFetch } from '../../utils/authFetch';
import { canEditCategory } from '../../utils/permissions';
import ModaleConferma from '../../components/ModaleConferma';

export default function CategoryEdit() {

  const navigate = useNavigate();
  const { id } = useParams();
  const selectedOrg = localStorage.getItem('selectedOrg');
  const isEditing = id !== 'nuova';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const [sections, setSections] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (!isEditing) return;

    const fetchCategory = async () => {
      try {
        const res = await authFetch(
          `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/category/${id}`,
          { credentials: 'include' }
        );

        if (!res.ok) throw new Error('Errore nel caricamento');
        const data = await res.json();
        const category = Array.isArray(data) ? data[0] : data;

        setName(category.name || '');
        setDescription(category.description || '');

        if (category.config) {
          const parsedConfig = JSON.parse(category.config);

          const normalizedConfig = parsedConfig.map((item) => ({
            ...item,
            ownership: item.ownership ?? 0,
            all: item.all ?? 0,
          }));

          setSections(normalizedConfig);
        }
      } catch (err) {
        console.error(err);
        toast.error('Errore durante il caricamento della categoria', {
          position: 'top-right',
        });
        navigate('/categorie');
      }
    };

    fetchCategory();
  }, [isEditing, id, selectedOrg, navigate]);

  const handleSubmit = async () => {
    const normalizedSections = sections.map((item) => ({
      ...item,
      ownership: item.ownership ?? 0,
      all: item.all ?? 0,
    }));

    const categories = JSON.stringify(normalizedSections);

    if (!name.trim()) {
      toast.error('Il nome è obbligatorio');
      return;
    }

    setLoading(true);

    try {
      const body = JSON.stringify({ name, description, categories });

      if (!isEditing) return;

      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/category/${id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body,
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Errore nel salvataggio');
      }

      toast.success(
        isEditing ? 'Categoria aggiornata' : 'Categoria creata',
        { position: 'top-right' }
      );
      navigate('/categorie');
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/category/${id}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!res.ok) throw new Error('Errore durante la cancellazione');

      toast.success('Categoria eliminata con successo');
      navigate('/categorie');
    } catch (err) {
      console.error(err);
      toast.error('Errore durante l\'eliminazione della categoria');
    } finally {
      setConfirmDeleteOpen(false);
    }
  };

  return (

        <Box>
          <Typography level="h3" mb={2}>
            {isEditing ? 'Modifica categoria' : 'Nuova categoria'}
          </Typography>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Button
              startDecorator={<ChevronLeft />}
              onClick={() => navigate(-1)}
              variant="outlined"
              size="sm"
            >
              Indietro
            </Button>
            {isEditing && canEditCategory() && (
              <Button
                variant="soft"
                color="danger"
                startDecorator={<Trash2 size={16} />}
                size="sm"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Elimina categoria
              </Button>
            )}
          </Box>

          <Box display="flex" flexDirection="column" gap={2}>
            <Input
              placeholder="Nome categoria"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={!canEditCategory()}
            />
            <Textarea
              placeholder="Descrizione (facoltativa)"
              minRows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEditCategory()}
            />
            <CategorySectionsEditor
              sections={sections}
              setSections={setSections}
              canEdit={canEditCategory()}
            />
            {canEditCategory() && (
              <Box display="flex" gap={2}>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleSubmit}
                  loading={loading}
                >
                  Salva
                </Button>
                <Button
                  variant="soft"
                  color="neutral"
                  onClick={() => navigate(-1)}
                >
                  Annulla
                </Button>
              </Box>
            )}
          </Box>

      <ModaleConferma
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Conferma eliminazione"
        message={<>
          Sei sicuro di voler eliminare questa categoria?
          <br /> Questa azione è irreversibile.
        </>}
      />
    </Box>
  );
}